import { DocumentType, gql } from "__generated__/gql";
import { useBrandMutation } from "utils/apollo/hooks/brand";
import DeleteConfirmModalButton from "components/Shared/ConfirmModal/DeleteConfirmModalButton";
import { assertPresence } from "utils/assertions";
import { removeDeletedRecords } from "utils/apollo/cacheHelpers";
import useAsyncCompleteToast from "components/Shared/useAsyncCompleteToast";

const deleteSkuMutation = gql(/* GraphQL */ `
  mutation DeleteSku($input: DeleteSkuInput!) {
    deleteSku(input: $input) {
      sku {
        id
        name
        productLine {
          id
        }
      }
    }
  }
`);

export type DeleteSkuData = DocumentType<typeof deleteSkuMutation>;

type Props = {
  onCompleted?: (data: DeleteSkuData) => void;
  sku: { id: UUID; name: string };
};

export default function DeleteSkuButton({ onCompleted, sku }: Props) {
  const { showSuccessToast } = useAsyncCompleteToast();

  const deleteMutation = useBrandMutation(deleteSkuMutation, {
    variables: { input: { id: sku.id } },
    onCompleted: (data) => {
      onCompleted?.(data);
      const deletedSku = data?.deleteSku?.sku;
      assertPresence(deletedSku);
      showSuccessToast(`Deleted ${deletedSku.name}`, {
        status: "warning",
      });
    },
    update: (cache, result) => {
      const deletedSku = result.data?.deleteSku?.sku;
      assertPresence(deletedSku?.productLine);
      removeDeletedRecords({
        cache,
        baseFieldName: "skus",
        deletedRecords: [deletedSku],
      });
      removeDeletedRecords({
        cache,
        baseFieldName: "skus",
        deletedRecords: [deletedSku],
        typename: "ProductLine",
        id: deletedSku.productLine.id,
      });
    },
  });

  return (
    <DeleteConfirmModalButton
      confirmText={
        <>
          Are you sure you want to delete the <b>{sku?.name}</b> SKU? You won’t
          be able to undo this action afterwards.
        </>
      }
      label="Permanently Delete SKU"
      mutation={deleteMutation}
      buttonProps={{ size: "md", opacity: 0.6 }}
      tooltipProps={{ placement: "left" }}
    />
  );
}
